import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
} from 'react';
import { useDispatch } from 'react-redux';
import {
    CHAT_BLK,
    CHAT_BLK_RESIZE_EVENT,
    CHAT_BLK_TOGGLE_EVENT,
    CHAT_BLK_TOGGLE_EVENT_CLOSE,
    CHAT_BLK_TOGGLE_EVENT_OPEN,
    SUMMARIZE_PROMPT,
} from '../constants';
import { useCache, useChatBlkStore } from '../hooks';

import { ChatBlkBaseService, ChatBlkTelemetryService } from '../services';
import { ChatBlkDispatch } from '../store/store';
import { sendQuery } from '../store/thunk';
import {
    ChatBLKToggleEvent,
    QueryTypeEnum,
    telemetryFunctionMap,
} from '../types';
import { removeUserData, saveUserData, uuidv4 } from '../utils';
import { useChatBlkContext } from './ChatBlkProvider';

interface GlobalEventListenerProps {
    handleCanvasToggle: (event: CustomEvent) => void;
    handleCanvasResized: (event: CustomEvent<{ panelWidth: number }>) => void;
    open: () => void;
    close: () => void;
}

const GlobalEventListenerContext = createContext<
    GlobalEventListenerProps | undefined
>(undefined);

export const useGlobalEventListenerContext = () => {
    const context = useContext(GlobalEventListenerContext);
    if (!context) {
        throw new Error(
            'useGlobalEventListenerContext must be used within a GlobalEventListenerProvider'
        );
    }
    return context;
};

/**
 * Custom hook that manages global event listeners and state for a chat canvas panel.
 *
 * @returns {Object} An object containing the current canvas width, panel open state, and a function to handle canvas toggle events.
 *
 * @property {number} canvasWidth - The current width of the chat canvas.
 * @property {function} handleCanvasToggle - A function to handle custom events for toggling the chat canvas.
 *
 * @description
 * This hook sets up and manages global event listeners for window resize, scroll, and custom chat canvas toggle events.
 * performance of event handlers.
 *
 * The hook uses the following dependencies:
 * - `useState` and `useEffect` from React for state management and side effects.
 * - `useCallback` for memoizing the `handlePanelHeight` function.
 * - `useAuthContext` for accessing authentication context.
 * - `useQueryDispatch` for dispatching queries.
 *
 * The hook performs the following tasks:
 * - Throttles the callback function to prevent it from being called multiple times within a specified time limit.
 * - Updates the height of the chat canvas panel based on the window scroll position.
 * - Updates the viewport width and host header selector based on the window inner width.
 * - Handles custom events for toggling the chat canvas panel.
 * - Tracks telemetry events for panel open/close and canvas resize.
 * - Adds responsive behavior to the chat canvas by changing its width based on the viewport width.
 * - Sets up and cleans up global event listeners for window resize, scroll, and custom chat canvas toggle events.
 */
export const GlobalEventListenerProvider = ({
    children,
}: {
    children: JSX.Element;
}): JSX.Element => {
    const chatService = ChatBlkBaseService.getInstance();
    const dispatch = useDispatch<ChatBlkDispatch>();
    const { storeCache, clearCache } = useCache();
    const { promptBoxRef } = useChatBlkContext();
    const {
        // setCurrentQueryId,
        // setIsProgressiveLoading,
        // setIsQueryCanceled,
        openPanel,
        closePanel,
    } = useChatBlkStore();

    /**
     * Handles the canvas resized event.
     *
     * @param event - The custom event containing the resize details.
     * @param event.detail - The details of the resize event.
     * @param event.detail.panelWidth - The new width of the canvas.
     *
     * This function updates the canvas width state and logs the event details.
     */
    const handleCanvasResized = useCallback(
        (event: CustomEvent<{ panelWidth: number }>): void => {
            const panelWidth = event.detail;
            console.log(`[${CHAT_BLK}] ${CHAT_BLK_RESIZE_EVENT}`, event.detail);
            const currentUserId = chatService.username;
            if (chatService.isLoggedIn) {
                saveUserData(currentUserId, 'settings', {
                    panelWidth,
                    fontSize: 'default',
                });
            } else {
                removeUserData(currentUserId, 'settings');
            }
        },
        [chatService]
    );

    /**
     * Handles the canvas toggle event.
     *
     * @param event - The custom event containing the toggle details.
     * @param event.detail - The details of the toggle event.
     * @param event.detail.action - The action to be performed (default is `CHAT_BLK_TOGGLE_EVENT_CLOSE`).
     * @param event.detail.relatedQuestion - The related question, if any.
     * @param event.detail.relatedQuestionId - The ID of the related question, if any.
     * @param event.detail.summarize - A flag indicating whether to summarize.
     *
     * This function sets the panel open state based on the action, logs the event details,
     * and performs different actions based on the user's login status. If the user is logged in,
     * it dispatches a query based on the event details and clears the chat cache. If the user is not logged in,
     * it caches the event details for later use.
     */
    const handleCanvasToggle = useCallback(
        async (event: CustomEvent<ChatBLKToggleEvent>): Promise<void> => {
            const {
                action = CHAT_BLK_TOGGLE_EVENT_CLOSE,
                relatedQuestion = '',
                relatedQuestionId = '',
                summarize,
            } = event.detail;

            action === CHAT_BLK_TOGGLE_EVENT_OPEN ? openPanel() : closePanel();

            console.log(`[${CHAT_BLK}] ${CHAT_BLK_TOGGLE_EVENT}`, event.detail);
            if (chatService.isLoggedIn) {
                if (summarize) {
                    await promptBoxRef.current?.setValue?.('');
                    ChatBlkTelemetryService.track(
                        telemetryFunctionMap.CHATBLK_MFE_FUNCTION_OPENED_FROM_SUMMARIZE
                    );
                    dispatch(
                        sendQuery({
                            prompt: SUMMARIZE_PROMPT,
                            telemetryFunction:
                                telemetryFunctionMap.CHATBLK_MFE_FUNCTION_PROMPT_BOX_SUMMARIZE_SUBMITTED,
                            queryType: QueryTypeEnum.SUMMARIZE,
                            chatExchangeId: uuidv4(),
                            hashId: uuidv4(),
                        })
                    );
                } else if (relatedQuestion) {
                    await promptBoxRef.current?.setValue?.('');
                    ChatBlkTelemetryService.track(
                        telemetryFunctionMap.CHATBLK_MFE_FUNCTION_OPENED_FROM_RELATED_QUESTION,
                        { relatedQuestionId }
                    );
                    dispatch(
                        sendQuery({
                            prompt: relatedQuestion,
                            chatExchangeId: relatedQuestionId,
                            hashId: uuidv4(),
                            telemetryFunction:
                                telemetryFunctionMap.CHATBLK_MFE_FUNCTION_PROMPT_BOX_RELATED_QUESTION_SUBMITTED,
                        })
                    );
                }
                clearCache(Object.keys(event.detail));
            } else {
                storeCache([
                    { key: 'action', value: action },
                    { key: 'relatedQuestion', value: relatedQuestion || '' },
                    {
                        key: 'relatedQuestionId',
                        value: relatedQuestionId || '',
                    },
                    { key: 'summarize', value: summarize ? 'true' : 'false' },
                ]);
            }
        },
        []
    );

    useEffect(() => {
        window.addEventListener(CHAT_BLK_RESIZE_EVENT, handleCanvasResized);
        window.addEventListener(CHAT_BLK_TOGGLE_EVENT, handleCanvasToggle);

        return () => {
            window.removeEventListener(
                CHAT_BLK_TOGGLE_EVENT,
                handleCanvasToggle
            );
            window.removeEventListener(
                CHAT_BLK_RESIZE_EVENT,
                handleCanvasResized
            );
        };
    }, [handleCanvasToggle, handleCanvasResized]);

    const values = useMemo(
        () => ({
            handleCanvasToggle,
            handleCanvasResized,
            open,
            close,
        }),
        [handleCanvasToggle, handleCanvasResized, open, close]
    );
    return (
        <GlobalEventListenerContext.Provider value={values}>
            {children}
        </GlobalEventListenerContext.Provider>
    );
};

GlobalEventListenerProvider.displayName = 'GlobalEventListenerProvider';
